import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, Renderer2 } from '@angular/core'
import { catchError, of, tap } from 'rxjs'
import Swal from 'sweetalert2'
import cloneDeep from 'lodash/cloneDeep'

import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { DocumentAPIModel } from 'src/app/models/api.model'
import { CommonService } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { DOCUMENT } from '@angular/common'

@Component({
  selector: 'app-attachment-list',
  templateUrl: './attachment-list.component.html',
  styleUrls: ['./attachment-list.component.scss']
})
export class AttachmentListComponent {
  @Input() label: string = 'Attachments'
  @Input() noAttactmentLabel: string = 'No Attachments Found'
  @Input() attachments: DocumentAPIModel[] = []
  @Input() companyEntityId: string = ''
  @Input() referenceId: string
  @Input() canDownloadAll: boolean = false

  @Output() deletedDocument: EventEmitter<DocumentAPIModel> = new EventEmitter<DocumentAPIModel>()

  previewDocData = {
    previewDocumentUrl: null,
    isPreviewVisible: false,
    index: null,
    hasNext: true,
    hasPrevious: true
  }

  constructor(
    private documentService: DocumentService,
    private commonService: CommonService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2
  ) {}

  deleteDocument(document: DocumentAPIModel, index: number): void {
    Swal.fire({
      title: `Do you want to delete ${document.name}?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'Yes',
      denyButtonText: 'No',
      color: '#000',
      confirmButtonColor: '#0452d7',
      denyButtonColor: '#444C66'
    }).then(result => {
      if (result.isConfirmed) {
        this.invokeDeleteDocumentAPI(document, index)
      }
    })
  }

  private invokeDeleteDocumentAPI(document: DocumentAPIModel, index: number): void {
    const { id, name } = document
    this.documentService
      .deleteDocument(id)
      .pipe(
        tap(() => {
          this.commonService.showSuccess(`Succesfully deleted ${name}`)
          this.deletedDocument.emit(document)
          this.deleteAttachmentByIndex(index)
        }),
        catchError((error: any) => {
          this.commonService.showError(`Unable to delete ${name}`)
          return of(null)
        })
      )
      .subscribe()
  }

  downloadDocument(document: DocumentAPIModel): void {
    const endPoint = `${ServiceUrlConstants.API_DOWNLOAD_DELETE_DOCUMENT}${document.id}`
    this.documentService.downloadDocument(endPoint)
  }

  private deleteAttachmentByIndex(index: number): void {
    const clonedAttachments = cloneDeep(this.attachments)
    clonedAttachments.splice(index, 1)
    this.attachments = clonedAttachments
  }

  downloadAllAttachments(): void {
    const endPoint = ServiceUrlConstants.createDownloadAllDocumentsEndpoint(this.referenceId)
    this.documentService.downloadDocument(endPoint)
  }

  previewDocument(document: DocumentAPIModel, index: number): void {
    const endPoint = `${ServiceUrlConstants.API_DOWNLOAD_DELETE_DOCUMENT}${document.id}`
    this.documentService.previewDocument(endPoint).subscribe(res => {
      if (res) {
        this.attachments = cloneDeep(this.attachments)
        this.attachments[index].documentURL = res
        this.updatePreviewDocData(res, index)
      }
    })
    this.renderer.addClass(this.document.body, 'noscroll')
  }

  closePreview(): void {
    this.previewDocData.isPreviewVisible = false
    this.resetPreviewDocData()
    this.renderer.removeClass(this.document.body, 'noscroll')
  }

  checkDocType(doc: any): boolean {
    const validDocTypes = ['docx', 'png', 'jpeg', 'jpg', 'pdf', 'doc']
    const docType = doc.name.split('.').pop().toLowerCase()
    return validDocTypes.includes(docType)
  }

  handleRequestDocument(direction: 'next' | 'previous'): void {
    const newIndex = direction === 'next' ? this.previewDocData.index + 1 : this.previewDocData.index - 1

    if (newIndex >= 0 && newIndex < this.attachments.length) {
      if (!this.checkDocType(this.attachments[newIndex])) {
        this.previewDocData.index = newIndex
        this.handleRequestDocument(direction)
        return
      }
      this.updateNavigationState(newIndex)
      if (this.attachments[newIndex].documentURL) {
        this.updatePreviewDocData(this.attachments[newIndex].documentURL, newIndex)
      } else {
        this.previewDocument(this.attachments[newIndex], newIndex)
      }
    } else {
      this.previewDocData.hasNext = false
    }
    this.changeDetectorRef.detectChanges()
  }

  handleRequestNextDocument(): void {
    this.handleRequestDocument('next')
  }

  handleRequestPreviousDocument(): void {
    this.handleRequestDocument('previous')
  }

  private updatePreviewDocData(url: string, index: number): void {
    this.previewDocData.previewDocumentUrl = url
    this.previewDocData.isPreviewVisible = true
    this.previewDocData.index = index
    this.previewDocData.hasPrevious = index > 0
    this.previewDocData.hasNext = index < this.attachments.length - 1
  }

  private resetPreviewDocData(): void {
    this.previewDocData.previewDocumentUrl = null
    this.previewDocData.index = null
  }

  private updateNavigationState(index: number): void {
    this.previewDocData.index = index
    this.previewDocData.hasPrevious = index > 0
    this.previewDocData.hasNext = index < this.attachments.length - 1
  }
}
